import { Buffer } from 'buffer';

import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { renderAsync } from 'docx-preview';
import React, { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';

import { CommonModal } from '../modal/modal';

import {
    ImagePreview,
    MessageBox,
    ModalSx,
    OfficePreviewBox,
    PreviewContainer,
    TextPreviewBox,
    TextPreviewTypography,
} from './DocumentPreviewModal.styles';

function stripWrappingQuotes(value: string): string {
    let start = 0;
    let end = value.length;
    while (start < end && (value[start] === '"' || value[start] === "'")) {
        start++;
    }
    while (end > start && (value[end - 1] === '"' || value[end - 1] === "'")) {
        end--;
    }
    return value.slice(start, end);
}

function getBase64FromPreviewData(previewData: string): string {
    if (!previewData) {
        console.log('⚠️ getBase64FromPreviewData: previewData is empty');
        return '';
    }

    console.log('🔍 getBase64FromPreviewData: input length:', previewData.length);

    const cleaned = stripWrappingQuotes(previewData.trim());

    // Handle data: URL format (e.g., "data:application/pdf;base64,...")
    if (cleaned.includes('base64,')) {
        const base64Part = cleaned.split('base64,')[1] || '';
        const trimmed = base64Part.replace(/[\r\n\t ]+/g, '');
        console.log('  ✓ Data URL format detected, extracted base64');
        console.log('    Base64 length:', trimmed.length);
        return trimmed;
    }

    // Plain base64 string
    const trimmed = cleaned.replace(/[\r\n\t ]+/g, '');
    console.log('  ℹ️ Plain base64 format');
    console.log('    Base64 length:', trimmed.length);
    return trimmed;
}

// ✅ safer base64 decode with better error messages
function safeAtob(base64: string): string {
    try {
        if (!base64 || base64.length === 0) {
            throw new Error('Empty base64 string');
        }
        console.log('🔐 Decoding base64, length:', base64.length);
        const decoded = atob(base64);
        console.log('  ✓ Base64 decoded successfully, binary length:', decoded.length);
        return decoded;
    } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        console.error('❌ Base64 decode error:', errorMsg);
        throw new Error(`Failed to decode base64: ${errorMsg}`);
    }
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = safeAtob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
}

// ✅ NEW: Blob conversion with better error handling (kept for legacy use, not needed for PDF)
function base64ToBlob(base64: string, contentType: string): Blob {
    if (!base64 || base64.length === 0) {
        throw new Error('Empty base64 string provided to base64ToBlob');
    }

    try {
        const binary = safeAtob(base64);
        const bytes = new Uint8Array(binary.length);

        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }

        const blob = new Blob([bytes], { type: contentType });

        if (blob.size === 0) {
            throw new Error('Created blob has size 0');
        }

        return blob;
    } catch (error) {
        console.error('base64ToBlob error:', error);
        throw error;
    }
}

const isSpreadsheetType = (type: string) => type.includes('spreadsheetml') || type === 'application/vnd.ms-excel';

type WordPreviewState = 'idle' | 'loading' | 'ready' | 'error';
type DocPreviewState = 'idle' | 'loading' | 'ready' | 'error';

interface DocumentPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    documentName?: string;
    documentDate?: string;
    previewData?: string;
    fileType: string;
    onDownload?: () => void;
    showDownloadButton?: boolean;
    primaryBtnName?: string;
    secondaryBtnName?: string;
}

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
    isOpen,
    onClose,
    documentName = '',
    documentDate = '',
    previewData,
    fileType,
    onDownload,
    showDownloadButton = false,
    primaryBtnName = 'Close',
    secondaryBtnName = 'Download',
}) => {
    const wordContainerRef = useRef<HTMLDivElement>(null);
    const pdfBlobUrlRef = useRef<string | null>(null);

    const [wordState, setWordState] = useState<WordPreviewState>('idle');
    const [docPreviewState, setDocPreviewState] = useState<DocPreviewState>('idle');
    const [docPreviewText, setDocPreviewText] = useState<string | null>(null);

    // ✅ PDF blob URL state (blob URLs bypass CSP restrictions better than data URLs)
    const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
    const [pdfError, setPdfError] = useState<string | null>(null);

    // ---------------- DOCX ----------------
    useEffect(() => {
        if (!isOpen || !previewData) {
            setWordState('idle');
            return;
        }

        const base64 = getBase64FromPreviewData(previewData);
        if (!base64) {
            console.error('❌ DOCX: Failed to extract base64 from previewData');
            setWordState('error');
            return;
        }

        if (!fileType.includes('wordprocessingml')) {
            console.warn('⚠️ DOCX: Incorrect fileType, expected wordprocessingml, got:', fileType);
            setWordState('error');
            return;
        }

        setWordState('loading');

        let cancelled = false;

        const run = () => {
            const container = wordContainerRef.current;
            if (!container || cancelled) {
                console.warn('⚠️ DOCX: Container not available or preview was cancelled');
                return;
            }

            container.innerHTML = '';
            const buffer = base64ToArrayBuffer(base64);

            renderAsync(buffer, container)
                .then(() => {
                    if (!cancelled) {
                        console.log('✓ DOCX: Preview rendered successfully');
                        setWordState('ready');
                    }
                })
                .catch(error => {
                    if (!cancelled) {
                        console.error('❌ DOCX: Rendering failed:', error);
                        setWordState('error');
                    }
                });
        };

        requestAnimationFrame(() => requestAnimationFrame(run));

        return () => {
            cancelled = true;
        };
    }, [isOpen, previewData, fileType]);

    // ---------------- DOC ----------------
    useEffect(() => {
        if (!isOpen || !previewData) {
            setDocPreviewState('idle');
            setDocPreviewText(null);
            return;
        }

        if (fileType !== 'application/msword') {
            console.warn('⚠️ DOC: Incorrect fileType, expected application/msword, got:', fileType);
            setDocPreviewState('error');
            setDocPreviewText(null);
            return;
        }

        const base64 = getBase64FromPreviewData(previewData);
        if (!base64) {
            console.error('❌ DOC: Failed to extract base64 from previewData');
            setDocPreviewState('error');
            setDocPreviewText(null);
            return;
        }

        setDocPreviewState('loading');

        import('word-extractor')
            .then(we => {
                type WordExtractorCtor = new () => { extract(b: Buffer): Promise<{ getBody(): string }> };
                const WordExtractorClass = ((we as { default?: unknown }).default ?? we) as WordExtractorCtor;
                const extractor = new WordExtractorClass();
                const buffer = Buffer.from(base64, 'base64');
                return extractor.extract(buffer);
            })
            .then(doc => {
                const text = doc.getBody() || '';
                console.log('✓ DOC: Preview extracted successfully, text length:', text.length);
                setDocPreviewText(text);
                setDocPreviewState('ready');
            })
            .catch(error => {
                console.error('❌ DOC: Preview extraction failed:', error);
                setDocPreviewState('error');
            });
    }, [isOpen, previewData, fileType]);

    // ✅ PDF (FIX: Convert base64 to Blob URL for CSP compliance) ----------------
    useEffect(() => {
        if (!previewData || fileType !== 'application/pdf') {
            if (pdfBlobUrlRef.current) URL.revokeObjectURL(pdfBlobUrlRef.current);
            setPdfBlobUrl(null);
            pdfBlobUrlRef.current = null;
            setPdfError(null);
            return;
        }

        console.log('📄 PDF Preview Effect - Starting PDF processing');
        console.log('  previewData length:', previewData.length);
        console.log('  fileType:', fileType);

        try {
            let base64 = previewData;

            // Extract base64 if it's a data URL
            if (previewData.startsWith('data:')) {
                console.log('✓ PDF data URL format detected, extracting base64');
                base64 = getBase64FromPreviewData(previewData);
            }

            if (!base64 || base64.length === 0) {
                throw new Error('Empty base64 string');
            }

            // Validate base64 format
            if (!/^[A-Za-z0-9+/=]*$/.test(base64)) {
                throw new Error('Invalid base64 format');
            }

            // Convert base64 to Blob
            const blob = base64ToBlob(base64, fileType);
            const blobUrl = URL.createObjectURL(blob);

            console.log('✓ Blob URL created successfully');
            console.log('  Blob size:', blob.size);
            console.log('  Blob URL:', blobUrl.substring(0, 50) + '...');

            pdfBlobUrlRef.current = blobUrl;
            setPdfBlobUrl(blobUrl);
            setPdfError(null);

            // Cleanup on unmount
            return () => {
                if (blobUrl) URL.revokeObjectURL(blobUrl);
            };
        } catch (error) {
            console.error('❌ PDF error:', error);
            setPdfError(error instanceof Error ? error.message : 'PDF processing failed');
            setPdfBlobUrl(null);
            pdfBlobUrlRef.current = null;
        }
    }, [previewData, fileType]);

    const renderPdf = () => {
        if (pdfError || !pdfBlobUrl) {
            return (
                <MessageBox>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Unable to preview PDF
                    </Typography>
                    <Typography sx={{ mb: 3, color: 'text.secondary' }}>
                        The PDF could not be displayed in the browser. Please try opening it in a new tab or download it
                        to view.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {pdfBlobUrl && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => window.open(pdfBlobUrl, '_blank')}
                                sx={{ minWidth: 150 }}
                            >
                                Open in New Tab
                            </Button>
                        )}
                        {onDownload && (
                            <Button variant="outlined" color="primary" onClick={onDownload} sx={{ minWidth: 150 }}>
                                Download File
                            </Button>
                        )}
                    </Box>
                </MessageBox>
            );
        }
        return (
            <>
                <iframe
                    src={pdfBlobUrl}
                    title={documentName ? `PDF preview: ${documentName}` : 'PDF preview'}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                />
                <Box sx={{ position: 'absolute', bottom: 10, right: 10, top: 5 }}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => window.open(pdfBlobUrl, '_blank')}
                        sx={{
                            backgroundColor: 'rgba(0, 30, 96, 0.9)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(0, 30, 96, 1)' },
                        }}
                    >
                        Open in New Tab
                    </Button>
                </Box>
            </>
        );
    };

    const renderText = (base64: string) => {
        try {
            return (
                <TextPreviewBox>
                    <TextPreviewTypography>{safeAtob(base64)}</TextPreviewTypography>
                </TextPreviewBox>
            );
        } catch {
            return (
                <MessageBox>
                    <Typography>Unable to display file</Typography>
                </MessageBox>
            );
        }
    };

    const renderExcel = (base64: string) => {
        try {
            const workbook = XLSX.read(base64, { type: 'base64' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const html = XLSX.utils.sheet_to_html(sheet);
            return (
                <OfficePreviewBox>
                    <div dangerouslySetInnerHTML={{ __html: html }} />
                </OfficePreviewBox>
            );
        } catch {
            return (
                <MessageBox>
                    <Typography>Excel preview failed</Typography>
                </MessageBox>
            );
        }
    };

    const renderDocx = () => (
        <OfficePreviewBox>
            {wordState === 'loading' && <CircularProgress />}
            {wordState === 'error' && <Typography>Preview failed. Download file.</Typography>}
            <div ref={wordContainerRef} />
        </OfficePreviewBox>
    );

    const renderDoc = () => {
        if (docPreviewState === 'loading') return <CircularProgress />;
        if (docPreviewState === 'error') return <Typography>Preview failed. Download file.</Typography>;
        return (
            <TextPreviewBox>
                <TextPreviewTypography>{docPreviewText}</TextPreviewTypography>
            </TextPreviewBox>
        );
    };

    const renderPreviewContent = () => {
        if (!previewData) return null;

        const base64 = getBase64FromPreviewData(previewData);

        if (fileType.startsWith('image/')) return <ImagePreview src={previewData} />;
        if (fileType === 'application/pdf') return renderPdf();
        if (fileType.startsWith('text/') || fileType === 'application/json' || fileType === 'application/xml')
            return renderText(base64);
        if (isSpreadsheetType(fileType)) return renderExcel(base64);
        if (fileType.includes('wordprocessingml')) return renderDocx();
        if (fileType.includes('msword')) return renderDoc();

        return <Typography>Preview not available</Typography>;
    };

    return (
        <CommonModal
            modalOpen={isOpen}
            onCloseModal={onClose}
            primaryBtnName={primaryBtnName}
            primaryBtnClick={onClose}
            showPrimaryButton
            secondaryBtnClick={onDownload}
            secondaryBtnName={secondaryBtnName}
            showButtons={showDownloadButton || !!onDownload}
            ModalMaxWidth="70%"
            ModalHeight="80%"
            sx={ModalSx}
            DialogMainTitle={documentName}
            DialogSubTitle={documentDate}
        >
            <PreviewContainer>{renderPreviewContent()}</PreviewContainer>
        </CommonModal>
    );
};

export default DocumentPreviewModal;
